import { useState, useEffect } from 'react';
import { css } from '@linaria/core';
import { getExistingFilterData } from '../../utils';
import classNames from 'classnames';
import { trackFilterSelection } from '../../utils/dataLayer';
import { screenReaderOnly } from '../../utils/styles';

const wrapper = css`
  padding: 0 0 10px 0;

  @media (min-width: 1080px) {
    padding: 0 0 10px 0;
  }
`;

const label = css`
  display: block;
  margin-bottom: 10px;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.02em;
  line-height: 30px;
`;

const radioGroup = css`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const radioOption = css`
  display: flex;
  align-items: center;
  gap: 15px;
  cursor: pointer;
`;

const radioControl = css`
  position: relative;
  display: inline-flex;
  flex: 0 0 auto;
  width: 30px;
  height: 30px;

  input {
    opacity: 0;
    width: 24px;
    height: 24px;
    margin: 0;
    cursor: pointer;
  }
`;

const radioIndicator = css`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-color: #fff;
  border: solid 2px #1a1a1a;
  border-radius: 50%;
  box-sizing: border-box;

  &:before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background-color: #1a1a1a;
    transform: translate(-50%, -50%) scale(0);
    transition: transform 0.15s ease-in-out;
  }

  input:checked + & {
    background-color: #fff;
    border-color: #1a1a1a;

    &:before {
      background-color: #1a1a1a;
      transform: translate(-50%, -50%) scale(1);
    }
  }

  input:focus + & {
    border: 3px solid #1a1a1a;
    outline: 3px solid #ffdd00;
  }
`;
const optionLabelText = css`
  font-size: 16px;
`;

const optionLabelTextActive = css`
  font-weight: 400;
`;

export default function RadioButtonFilter(props) {
  const {
    fieldId,
    filters,
    id,
    label: groupLabel,
    options,
    removeFilter,
    setFilter,
  } = props;

  const [selected, setSelected] = useState('all');

  useEffect(() => {
    const existingValue = getExistingFilterData(fieldId, filters);

    if (!existingValue) {
      setSelected('all');
    } else if (existingValue.includes('true')) {
      setSelected('true');
    } else if (existingValue.includes('false')) {
      setSelected('false');
    } else {
      setSelected('all');
    }
  }, [filters, fieldId]);

  const handleChange = (optionValue, optionLabel) => {
    setSelected(optionValue === undefined ? 'all' : optionValue);

    if (optionValue === undefined) {
      removeFilter(fieldId);
    } else {
      setFilter(fieldId, optionValue);
      trackFilterSelection(fieldId, optionLabel);
    }
  };

  return (
    <div className={wrapper}>
      <span className={label} id={`${id}-label`}>
        {groupLabel}:
      </span>

      <div
        aria-labelledby={`${id}-label`}
        className={radioGroup}
        role="radiogroup"
      >
        {options.map((option, index) => {
          const optionKey = option.value === undefined ? 'all' : option.value;
          const inputId = `${id}-${optionKey}`;
          const isChecked = selected === optionKey;

          return (
            <label className={radioOption} htmlFor={inputId} key={index}>
              <span className={radioControl}>
                <input
                  checked={isChecked}
                  id={inputId}
                  name={id}
                  onChange={() => handleChange(option.value, option.label)}
                  type="radio"
                  value={optionKey}
                />
                <span aria-hidden="true" className={radioIndicator}></span>
              </span>

              <span
                className={classNames(
                  optionLabelText,
                  isChecked && optionLabelTextActive,
                )}
              >
                {option.label}
              </span>
              <span className={screenReaderOnly}>{option.label}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}
